
import { BaseService } from './base-service';
import { Trip } from '../types';

export class TripService extends BaseService {
  public async getUserTrips(): Promise<Trip[]> {
    if (this.heyUtils._cachedTrips) {
      if (Date.now() - this.heyUtils._cachedTripsTimestamp < 1000 * 60 * 4) {
        return this.heyUtils._cachedTrips;
      } else {
        this.heyUtils._cachedTrips = null;
      }
    }
    
    let result = await this.callHeyApiUser('GET', `/v3/user/${this.heyUtils.user_id}/trips`, null);
    
    if (!result.success) {
      return null;
    }
    
    if (result?.data?.data?.trips === undefined) {
      return null;
    }
    
    this.heyUtils._cachedTrips = result.data.data.trips as Trip[];
    this.heyUtils._cachedTripsTimestamp = Date.now();
    return result.data.data.trips as Trip[];
  }

  public async getOfferById(offerId: string): Promise<any> {
    return null; // TODO: Implement this method, API is not the correct one (we have to target gateway directly)
    console.debug('🔍 [TripService] Fetching offer by ID:', offerId);
    let result = await this.callHeyApiUser('GET', `/offers/public/${offerId}/full`, null);
    
    if (!result.success) {
      return null;
    }
    console.log("Offer result:", result.data);
    return result.data; 
  }

  public async importAdtdTrip(adtdData: any): Promise<Trip> {
    console.debug('🔄 [TripService] Importing ADTD trip:', adtdData)
    let result = await this.callHeyApiUser('POST', `/v3/user/${this.heyUtils.user_id}/adtd/trip`, adtdData);
    if (!result.success) {
      console.error('❌ [TripService] Failed to import ADTD trip:', result);
      return null;
    }

    if (!result.data?.data?.trip) {
      console.warn('⚠️ [TripService] Trip data is empty in successful response');
      return null;
    }
    
    return result.data.data.trip as Trip;
  }

  public async addTripByReference(reference: string): Promise<Trip> {
    console.debug('🔄 [TripService] Adding trip by reference:', reference);
    
    let result = await this.callHeyApiUser('POST', `/v3/user/${this.heyUtils.user_id}/trip/${reference}`, null);
    
    if (!result.success) {
      console.error('❌ [TripService] Failed to add trip by reference:', result);
      
      // Check for specific error case (422 with specific PNR status and failure reason)
      if (result.status === 422) {
        console.warn('⚠️ [TripService] Received 422 status code');
        
        // Log the full structure of the error response to understand its format
        console.debug('📋 [TripService] Full error response (stringified):', JSON.stringify(result, null, 2));
        console.debug('📋 [TripService] Error data structure:', result.data);
        
        // Check different possible paths for the error data
        if (result.data?.pnrStatus === "UNAVAILABLE_PNR" && result.data?.pnrFailureReason === "NOT_1A_PNR") {
          console.error('🚫 [TripService] Profile mismatch detected from data.pnrStatus');
          throw new Error("PROFILE_MISMATCH");
        }
        
        // Also check if error info might be in the error field
        if (typeof result.error === 'string' && result.error.includes("UNAVAILABLE_PNR")) {
          console.error('🚫 [TripService] Profile mismatch detected from error string');
          throw new Error("PROFILE_MISMATCH");
        }
        
        // Try to find these fields anywhere in the response
        const resultStr = JSON.stringify(result);
        if (resultStr.includes("UNAVAILABLE_PNR") && resultStr.includes("NOT_1A_PNR")) {
          console.error('🚫 [TripService] Profile mismatch detected from string search');
          throw new Error("PROFILE_MISMATCH");
        }
      }
      return null;
    }
    
    console.debug('✅ [TripService] Trip added successfully');
    
    if (!result.data.data.trip) {
      console.warn('⚠️ [TripService] Trip data is empty in successful response');
      return null;
    }
    
    this.heyUtils._cachedTrips = null;
    this.heyUtils._cachedTripsTimestamp = 0;
    
    return result.data.data.trip as Trip;
  }

  public async addTripManually(tripData: Trip): Promise<Trip> {
    console.debug('Adding trip manually:', tripData);
    
    if (tripData.products) {
      tripData.products.forEach(product => {
        if (product.air) {
          if (product.air.number && typeof product.air.number === 'string') {
            product.air.number = product.air.number.trim();
          }
          
          if (product.air.marketing?.flightDesignator?.flightNumber) {
            product.air.marketing.flightDesignator.flightNumber = 
              product.air.marketing.flightDesignator.flightNumber.trim();
          }
          
          if (product.air.operating?.flightDesignator?.flightNumber) {
            product.air.operating.flightDesignator.flightNumber = 
              product.air.operating.flightDesignator.flightNumber.trim();
          }
        }
      });
    }
    
    let result = await this.callHeyApiUser('POST', `/v3/user/${this.heyUtils.user_id}/trip`, {
      trip: tripData,
      userId: this.heyUtils.user_id
    });
    
    console.debug('Add trip result:', result);
    
    if (!result.success) {
      throw new Error('Failed to add trip: ' + result.error);
    }
    
    if (!result.data?.data?.trip) {
      throw new Error('Failed to add trip. No trip data returned.');
    }
    
    this.heyUtils._cachedTrips = null;
    this.heyUtils._cachedTripsTimestamp = 0;
    
    return result.data.data.trip as Trip;
  }

  public async removeTrip(reference: string): Promise<boolean> {
    console.debug('Removing trip:', reference);
    
    let result = await this.callHeyApiUser('DELETE', `/v3/user/${this.heyUtils.user_id}/trip/${reference}`, null);
    
    this.heyUtils._cachedTrips = null;
    this.heyUtils._cachedTripsTimestamp = 0;
    
    console.debug('Remove trip result:', result);
    return result.success;
  }
  
  public async getAirlineCheckinUrls(reference: string): Promise<any> {
    console.debug('🌐 [TripService] Fetching airline checkin URLs for trip:', reference);
    
    // Check if we have user id
    if (!this.heyUtils.user_id) {
      console.error('❌ [TripService] Cannot fetch checkin URLs: No user ID available');
      return null;
    }
    
    const cacheKey = `trip_checkin_${reference}`;
    console.debug(`🔍 [TripService] Checking local storage cache for key: ${cacheKey}`);
    
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      try {
        console.debug(`🗄️ [TripService] Found cached data for trip ${reference}`);
        const parsedData = JSON.parse(cachedData);
        const cacheTimestamp = parsedData.timestamp;
        const currentTime = Date.now();
        const cacheAge = currentTime - cacheTimestamp;
        const cacheLimit = 1000 * 60 * 60; // 1 hour
        
        console.debug(`⏱️ [TripService] Cache age: ${Math.round(cacheAge / 1000 / 60)} minutes, limit: ${cacheLimit / 1000 / 60} minutes`);
        
        if (cacheAge < cacheLimit) {
          console.debug(`✅ [TripService] Using cached checkin URLs for trip ${reference}`);
          return parsedData.data;
        } else {
          console.debug(`🕒 [TripService] Cache expired for trip ${reference}, fetching fresh data`);
        }
      } catch (error) {
        console.error('❌ [TripService] Error parsing cached checkin URLs:', error);
      }
    } else {
      console.debug(`🔍 [TripService] No cache found for trip ${reference}`);
    }
    
    const endpoint = `/v3/users/${this.heyUtils.user_id}/trips/${reference}/airline-checkin-url-redirections`;
    console.debug(`🌐 [TripService] Calling API endpoint: ${endpoint}`);
    
    let result;
    try {
      result = await this.callHeyApiUser('GET', endpoint, null);
      console.debug(`📡 [TripService] API response for checkin URLs:`, result);
    } catch (error) {
      console.error(`❌ [TripService] API call failed:`, error);
      return null;
    }
    
    if (!result.success || !result.data) {
      console.error('❌ [TripService] Failed to fetch checkin URLs:', result.error || 'Unknown error');
      return null;
    }
    
    console.debug(`💾 [TripService] Saving checkin URLs to local storage with key ${cacheKey}`);
    
    const storageData = {
      timestamp: Date.now(),
      data: result.data
    };
    
    try {
      localStorage.setItem(cacheKey, JSON.stringify(storageData));
      console.debug(`✅ [TripService] Successfully cached checkin URLs`);
    } catch (error) {
      console.error(`❌ [TripService] Error saving to localStorage:`, error);
      localStorage.clear();
    }
    
    return result.data;
  }
}
